/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import useFluxibleAction from 'js/lib/fluxibleHooks/useFluxibleAction';
import useFluxibleStore from 'js/lib/fluxibleHooks/useFluxibleStore';
import Retracked from 'js/lib/retracked';

import type { ButtonName } from '@coursera/event-pulse-types';

import type { FeedbackComments } from 'bundles/authoring-course-content-feedback/types/courseContentFeedback';
import { CMLUtils } from 'bundles/cml';
import { flagItem } from 'bundles/content-feedback/actions/ItemFeedbackActions';
import Flag from 'bundles/content-feedback/components/cds/flag/Flag';
import type { ItemType } from 'bundles/content-feedback/constants/ItemTypes';
import ItemFeedbackStore from 'bundles/content-feedback/stores/ItemFeedbackStore';
import {
  getFeedbackCommentCategoryKey,
  getItemFeedbackEventContext,
  mapItemTypeToCourseItemType,
} from 'bundles/content-feedback/utils/ItemFeedbackUtils';
import CourseStore from 'bundles/in-course-stores/stores/CourseStore';
import type { Item } from 'bundles/learner-progress/types/Item';
import type { ComputedItemType } from 'bundles/ondemand/lib/ComputedItem';
import { track as trackV3 } from 'bundles/page/lib/event-pulse/sdk';
import userPreferences from 'bundles/video-player/utils/userPreferences';

type PropsFromCaller = {
  computedItem: Item | ComputedItemType;
  itemFeedbackType: ItemType;
  tooltipPlacement: string;
  subItemId?: string;
  eventNameSubmit: ButtonName;
  eventNameRemove: ButtonName;
  eventNameOpen: ButtonName;
  /**
   * Relevant for course lecture item pages in the context of the "Report an Issue" button. We need this to set whether we want to cancel autoplay
   * of the video when a user is trying to report an issue.
   */
  setIsUserReportingIssue?: (isUserReportingIssue: boolean) => void;
};

export type Props = PropsFromCaller;

export const VideoItemFeedbackFlag = ({
  computedItem,
  itemFeedbackType,
  tooltipPlacement,
  subItemId,
  eventNameSubmit,
  eventNameRemove,
  eventNameOpen,
  setIsUserReportingIssue,
}: Props) => {
  const executeAction = useFluxibleAction();

  const { courseId } = useFluxibleStore(CourseStore, (store) => ({
    courseId: store.getCourseId(),
  }));

  const { flagFeedback, feedbackItemId } = useFluxibleStore(ItemFeedbackStore, (store) => ({
    flagFeedback: store.getFlagFeedback(),
    feedbackItemId: store.getItemId(),
  }));

  // Using the same eventing data as the ItemFeedback.tsx with the legacy event tracking to maintain
  // the existing events since there are data pipelines and queries that depend on it. There are
  // no current efforts to migrate the legacy events to eventing v3
  const track = Retracked.makeTracker(getItemFeedbackEventContext(feedbackItemId, courseId));

  const trackV3Flag = (
    buttonName: ButtonName,
    initalFeedbackComments: FeedbackComments,
    submittedFeedbackComment: FeedbackComments
  ) => {
    const { courseSlug, name } = computedItem;
    const category = getFeedbackCommentCategoryKey(initalFeedbackComments, submittedFeedbackComment);

    trackV3('click_button', {
      button: { name: buttonName },
      product: { id: courseId, slug: courseSlug, name },
      courseItem: {
        id: feedbackItemId,
        type: mapItemTypeToCourseItemType(itemFeedbackType),
        feedback: { issueCategory: category },
      },
    });
  };

  const handleFlag = () => {
    const { courseSlug, name } = computedItem;

    trackV3('click_button', {
      button: { name: eventNameOpen },
      product: { id: courseId, slug: courseSlug, name },
      courseItem: {
        id: feedbackItemId,
        type: mapItemTypeToCourseItemType(itemFeedbackType),
      },
    });
    track('click.flag');
    setIsUserReportingIssue?.(true);
  };

  const submitFlag = (comments: FeedbackComments, eventKey?: string) => {
    const { id: itemId } = computedItem;
    const categories: Record<string, number> = {};
    const active = Object.keys(comments).length > 0;

    const audioLanguage = userPreferences.get('audioLanguage');
    const dubbedSubtitleLanguage = userPreferences.get('dubbedSubtitleLanguage');
    const subtitleLanguage = userPreferences.get('subtitleLanguage');

    const videoPlayerUserPreferences = {
      audioLanguage,
      dubbedSubtitleLanguage,
      subtitleLanguage,
    };

    const itemSpecificFeedback = {
      lecture: {
        videoPlayerUserPreferences,
      },
    };

    executeAction(flagItem, {
      courseId,
      itemId,
      comments,
      subItemId,
      active,
      itemSpecificFeedback,
    });

    Object.entries(comments).forEach(([type, comment]) => {
      if (comment) {
        categories[type] = CMLUtils.getLength(comment);
      }
    });

    track(eventKey, {
      // eslint-disable-next-line camelcase
      flag_categories: categories,
    });

    setIsUserReportingIssue?.(false);
  };

  const handleFlagSubmit = (comments: FeedbackComments) => {
    trackV3Flag(eventNameSubmit, flagFeedback.comments, comments);
    submitFlag(comments, 'click.send_flag_comments');
  };

  const handleFlagRemove = (comments: FeedbackComments) => {
    trackV3Flag(eventNameRemove, comments, flagFeedback.comments);
    submitFlag(comments, 'click.remove_flag_comment');
  };

  const handleFlagCancel = () => {
    setIsUserReportingIssue?.(false);
  };

  return (
    <div>
      <Flag
        computedItem={computedItem}
        itemFeedbackType={itemFeedbackType}
        selected={flagFeedback.isActive}
        comments={flagFeedback.comments}
        tooltipPlacement={tooltipPlacement}
        onSelect={handleFlag}
        onSubmit={handleFlagSubmit}
        onRemove={handleFlagRemove}
        onCancel={handleFlagCancel}
      />
    </div>
  );
};

export default VideoItemFeedbackFlag;
